import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthProvider'
import { getInitials } from '@/lib/utils'
import logoImg from '@/assets/logo.png'

interface NavItem {
  to: string
  label: string
  icon: React.ReactNode
  minNivel?: number
}

const NAV_SECTIONS: { title: string; items: NavItem[] }[] = [
  {
    title: 'Principal',
    items: [
      { to: '/', label: 'Dashboard', icon: <ChartIcon /> },
      { to: '/caja', label: 'Caja / Ventas', icon: <CashIcon />, minNivel: 2 },
      { to: '/ventas/historial', label: 'Ventas', icon: <ListIcon />, minNivel: 2 },
    ],
  },
  {
    title: 'Inventario',
    items: [
      { to: '/productos', label: 'Productos', icon: <BoxIcon /> },
      { to: '/stock', label: 'Stock', icon: <StockIcon /> },
      { to: '/compras', label: 'Compras', icon: <TruckIcon />, minNivel: 3 },
      { to: '/retiros', label: 'Retiros', icon: <PackageIcon />, minNivel: 3 },
    ],
  },
  {
    title: 'Clientes',
    items: [
      { to: '/clientes', label: 'Clientes', icon: <UsersIcon />, minNivel: 2 },
      { to: '/clientes/deudas', label: 'Cuenta corriente', icon: <ReceiptIcon />, minNivel: 2 },
    ],
  },
  {
    title: 'Análisis',
    items: [
      { to: '/reportes', label: 'Reportes', icon: <ReportIcon /> },
      { to: '/asistente', label: 'Asistente IA', icon: <AIIcon /> },
    ],
  },
]

const ADMIN_ITEMS: NavItem[] = [
  { to: '/admin/empleados', label: 'Empleados', icon: <UsersIcon />, minNivel: 1 },
  { to: '/admin/roles', label: 'Roles', icon: <ShieldIcon />, minNivel: 1 },
  { to: '/admin/movimientos', label: 'Movimientos', icon: <ActivityIcon />, minNivel: 1 },
  { to: '/categorias', label: 'Categorias', icon: <TagIcon />, minNivel: 1 },
  { to: '/depositos', label: 'Depositos', icon: <WarehouseIcon />, minNivel: 1 },
  { to: '/configuracion', label: 'Configuracion', icon: <SettingsIcon />, minNivel: 1 },
  { to: '/usuarios', label: 'Usuarios', icon: <UserGroupIcon />, minNivel: 1 },
]

export default function Sidebar() {
  const { profile, rolNivel, signOut } = useAuth()
  const navigate = useNavigate()

  async function handleSignOut() {
    await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <nav className="me-sidebar">
      {/* Logo */}
      <div className="me-sidebar-header">
        <MELogo />
      </div>

      {/* Nav sections */}
      <div style={{ flex: 1, padding: '8px 10px', overflowY: 'auto' }}>
        {NAV_SECTIONS.map(section => {
          const visibleItems = section.items.filter(
            item => !item.minNivel || (rolNivel !== null && rolNivel <= item.minNivel)
          )
          if (!visibleItems.length) return null
          return (
            <div key={section.title}>
              <div className="me-nav-section">{section.title}</div>
              {visibleItems.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `me-nav-item${isActive ? ' active' : ''}`
                  }
                >
                  <span style={{ width: 16, height: 16, flexShrink: 0 }}>{item.icon}</span>
                  {item.label}
                </NavLink>
              ))}
            </div>
          )
        })}

        {rolNivel === 1 && (
          <div>
            <div className="me-nav-section">Administracion</div>
            {ADMIN_ITEMS.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `me-nav-item${isActive ? ' active' : ''}`
                }
              >
                <span style={{ width: 16, height: 16, flexShrink: 0 }}>{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>

      {/* User footer */}
      <div className="me-sidebar-footer">
        <Avatar name={profile?.nombre ?? '?'} size={34} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'var(--me-sidebar-text-hi)',
          }}>
            {profile?.nombre ?? 'Usuario'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--me-sidebar-text)', textTransform: 'capitalize' }}>
            {profile?.roles?.nombre ?? ''}
          </div>
        </div>
        <button
          onClick={handleSignOut}
          title="Cerrar sesion"
          className="me-sidebar-action"
        >
          <LogoutIcon />
        </button>
      </div>
    </nav>
  )
}

function Avatar({ name, size = 32 }: { name: string; size?: number }) {
  const initials = getInitials(name)
  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: size * 0.32,
      background: 'var(--me-acid)',
      color: 'var(--me-acid-ink)',
      fontFamily: 'var(--me-display)',
      fontWeight: 700,
      fontSize: size * 0.4,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      letterSpacing: '-0.02em',
      flexShrink: 0,
    }}>
      {initials}
    </div>
  )
}

function MELogo() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      <img
        src={logoImg}
        alt="MayoExpress"
        style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, objectFit: 'cover' }}
      />
      <span style={{
        fontFamily: 'var(--me-display)',
        fontVariationSettings: '"opsz" 24, "wght" 700',
        fontSize: 16,
        letterSpacing: '-0.03em',
        color: 'var(--me-sidebar-text-hi)',
      }}>MayoExpress</span>
    </div>
  )
}

/* ── Iconos inline (16x16, stroke 1.6) ── */
const I = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
const Ic = (d: React.ReactNode) => <svg viewBox="0 0 24 24" width={16} height={16} {...I}>{d}</svg>

function ChartIcon() { return Ic(<><path d="M4 20V8M10 20v-8M16 20v-5M22 20H2"/></>) }
function ListIcon() { return Ic(<><path d="M8 6h13M8 12h13M8 18h13M4 6v.01M4 12v.01M4 18v.01"/></>) }
function CashIcon() { return Ic(<><rect x="3" y="6" width="18" height="12" rx="2"/><circle cx="12" cy="12" r="3"/><path d="M6 9.5v.01M18 14.5v.01"/></>) }
function BoxIcon() { return Ic(<><path d="M4 8l8-4 8 4v8l-8 4-8-4V8z"/><path d="M4 8l8 4 8-4M12 12v8"/></>) }
function StockIcon() { return Ic(<><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-4 0v2M8 7V5a2 2 0 014 0"/><path d="M12 12v4M10 14h4"/></>) }
function TruckIcon() { return Ic(<><path d="M3 7h11v9H3V7zM14 11h4l3 3v2h-7v-5z"/><circle cx="7" cy="18" r="1.5"/><circle cx="17" cy="18" r="1.5"/></>) }
function PackageIcon() { return Ic(<><path d="M12 2l8 4v8l-8 4-8-4V6l8-4z"/><path d="M4 6l8 4 8-4M12 10v8"/><path d="M8 4l8 4"/></>) }
function UsersIcon() { return Ic(<><circle cx="9" cy="7" r="3"/><path d="M3 20c1-4 12-4 12 0"/><circle cx="18" cy="8" r="2.5"/><path d="M20 20c.5-2 4-2 4 0"/></>) }
function ReceiptIcon() { return Ic(<><path d="M4 2v20l2-2 2 2 2-2 2 2 2-2 2 2 2-2V2"/><path d="M8 7h8M8 11h8M8 15h5"/></>) }
function ReportIcon() { return Ic(<><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M7 16l4-4 3 3 3-6"/></>) }
function SettingsIcon() { return Ic(<><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"/></>) }
function UserGroupIcon() { return Ic(<><circle cx="12" cy="8" r="3.5"/><path d="M5 20c1.5-3.5 4-5 7-5s5.5 1.5 7 5"/></>) }
function LogoutIcon() { return Ic(<><path d="M14 4h5v16h-5M9 8l-4 4 4 4M5 12h11"/></>) }
function TagIcon() { return Ic(<><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><circle cx="7" cy="7" r="1.5"/></>) }
function WarehouseIcon() { return Ic(<><rect x="2" y="9" width="20" height="12" rx="1"/><path d="M12 3L2 9h20L12 3z"/><path d="M10 21v-6h4v6"/></>) }
function AIIcon() { return Ic(<><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/><path d="M8 10h.01M12 10h.01M16 10h.01"/></>) }
function ShieldIcon() { return Ic(<><path d="M12 2l7 4v5c0 4.5-3 8.5-7 10C8 19.5 5 15.5 5 11V6l7-4z"/></>) }
function ActivityIcon() { return Ic(<><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></>) }
